import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Bot, MessageCircle, Minus, RotateCcw, Send, Sparkles, X } from "lucide-react";
import { api } from "@/services/apiClient";
import { BOT_NAME, BOT_TAGLINE, INTENTS, fallbackResponse, findIntent, intentById, normalizeSynccbotReply } from "@/lib/botKB";

type ChatRole = "user" | "bot";

type ChatMessage = {
  role: ChatRole;
  text: string;
  suggestedIds?: string[];
  ts: number;
  intentId?: string;
  source?: string;
};

const STORAGE_KEY = "amos_bot_conversation_v1";
const SEEN_KEY = "amos_bot_seen";
const POS_KEY = "amos_bot_position_v1";

/** Keep the widget at least this far from any viewport edge. */
const EDGE_PX = 12;
/** A pointer must travel this far before it counts as a drag rather than a tap. */
const DRAG_THRESHOLD_PX = 4;

/**
 * Where the widget sits, stored as the distance from the bottom-right corner in viewport
 * pixels. Anchoring to that corner means the position survives a window resize without
 * having to re-derive it, and it is the same value for the launcher and the open panel.
 */
type WidgetPos = { right: number; bottom: number };

function defaultPos(): WidgetPos {
  return { right: 20, bottom: 20 };
}

function loadPos(): WidgetPos {
  try {
    const raw = localStorage.getItem(POS_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<WidgetPos>) : null;
    if (
      parsed &&
      Number.isFinite(parsed.right) &&
      Number.isFinite(parsed.bottom) &&
      Number(parsed.right) >= 0 &&
      Number(parsed.bottom) >= 0
    ) {
      return { right: Number(parsed.right), bottom: Number(parsed.bottom) };
    }
  } catch {
    // corrupt or unavailable storage — fall back to the default corner
  }
  return defaultPos();
}

function savePos(pos: WidgetPos): void {
  try {
    localStorage.setItem(POS_KEY, JSON.stringify(pos));
  } catch {
    // no-op
  }
}

/** Keep the widget fully on screen, whatever the viewport size. */
function clampToViewport(pos: WidgetPos, width: number, height: number): WidgetPos {
  const maxRight = Math.max(EDGE_PX, window.innerWidth - width - EDGE_PX);
  const maxBottom = Math.max(EDGE_PX, window.innerHeight - height - EDGE_PX);
  return {
    right: Math.min(Math.max(EDGE_PX, pos.right), maxRight),
    bottom: Math.min(Math.max(EDGE_PX, pos.bottom), maxBottom),
  };
}

const GREETING: ChatMessage = {
  role: "bot",
  text: `Hi! I'm ${BOT_NAME}. ${BOT_TAGLINE}. Ask me anything about MarketIQ, or pick a suggestion below.`,
  suggestedIds: ["what_is_2io", "content_studio", "publish_website", "seo_hub"],
  ts: Date.now(),
};

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? (JSON.parse(raw) as ChatMessage[]) : null;
    if (!arr || !arr.length) return [GREETING];
    return arr;
  } catch {
    return [GREETING];
  }
}

function saveHistory(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-100)));
  } catch {
    // no-op
  }
}

function buildChatHistory(messages: ChatMessage[]): Array<{ role: ChatRole; text: string }> {
  return messages
    .filter((m) => (m.role === "user" || m.role === "bot") && m.text)
    .map((m) => ({ role: m.role, text: String(m.text) }))
    .slice(-20);
}

function renderRich(text: string) {
  const parts = [];
  let remaining = text || "";
  let i = 0;
  while (remaining.length) {
    const bold = remaining.match(/^\*\*(.+?)\*\*/);
    if (bold) {
      parts.push(<strong key={i++}>{bold[1]}</strong>);
      remaining = remaining.slice(bold[0].length);
      continue;
    }
    const code = remaining.match(/^`([^`]+)`/);
    if (code) {
      parts.push(
        <code key={i++} className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[12px] text-zinc-800">
          {code[1]}
        </code>,
      );
      remaining = remaining.slice(code[0].length);
      continue;
    }
    const next = remaining.search(/\*\*|`/);
    const chunk = next === -1 ? remaining : remaining.slice(0, next);
    parts.push(<span key={i++}>{chunk}</span>);
    remaining = next === -1 ? "" : remaining.slice(next);
  }
  return parts;
}

export function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(loadHistory);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Where the widget sits. Persisted, so it stays where the user parked it.
  const [pos, setPos] = useState<WidgetPos>(loadPos);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ startX: number; startY: number; startPos: WidgetPos } | null>(null);
  // Set once a pointer actually moves, so the release does not also fire the click.
  const dragged = useRef(false);

  useEffect(() => {
    savePos(pos);
  }, [pos]);

  /** Keep the widget on screen when the window shrinks. */
  useEffect(() => {
    const reclamp = () => {
      const el = open ? panelRef.current : launcherRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos((p) => clampToViewport(p, rect.width, rect.height));
    };
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [open]);

  /**
   * Start dragging. `skipInteractive` is set when the drag starts from the panel header,
   * whose own buttons (reset, close) must stay clickable.
   */
  const beginDrag = (event: ReactPointerEvent<HTMLElement>, skipInteractive = false) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (skipInteractive && (event.target as HTMLElement).closest("button, a, input")) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startY: event.clientY, startPos: pos };
    dragged.current = false;
  };

  const moveDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    if (
      !dragged.current &&
      Math.abs(dx) < DRAG_THRESHOLD_PX &&
      Math.abs(dy) < DRAG_THRESHOLD_PX
    ) {
      return;
    }
    dragged.current = true;
    const rect = event.currentTarget.getBoundingClientRect();
    setPos(
      clampToViewport(
        { right: state.startPos.right - dx, bottom: state.startPos.bottom - dy },
        rect.width,
        rect.height,
      ),
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    drag.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // the pointer may already be released (cancel)
    }
  };

  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages, typing]);

  useEffect(() => {
    if (!localStorage.getItem(SEEN_KEY)) setHasUnread(true);
  }, []);

  const appendStaticBotForQuery = (query: string) => {
    setMessages((m) => {
      const intent = findIntent(query);
      if (intent) {
        return [
          ...m,
          {
            role: "bot",
            text: intent.answer,
            suggestedIds: intent.suggested || [],
            ts: Date.now(),
            intentId: intent.id,
          },
        ];
      }
      const fb = fallbackResponse();
      return [...m, { role: "bot", text: fb.text, suggestedIds: fb.suggestedIds, ts: Date.now() }];
    });
  };

  const respondTo = async (query: string) => {
    let history: Array<{ role: ChatRole; text: string }> = [];
    setMessages((prev) => {
      history = buildChatHistory(prev);
      return [...prev, { role: "user", text: query, ts: Date.now() }];
    });
    setTyping(true);
    const delay = 450 + Math.min(900, query.length * 20);

    try {
      const { data } = await api.post(
        "/ai/synccbot/chat",
        { message: query, history },
        { timeout: 65000 },
      );
      const normalized = normalizeSynccbotReply(
        (data as { reply?: unknown })?.reply,
        (data as { source?: unknown })?.source,
      );
      if (normalized.reply) {
        const suggestedIds = normalized.source === "fallback" ? ["what_is_2io", "content_studio", "seo_hub"] : [];
        setMessages((m) => [
          ...m,
          { role: "bot", text: normalized.reply, suggestedIds, ts: Date.now(), source: normalized.source },
        ]);
      } else {
        appendStaticBotForQuery(query);
      }
    } catch {
      window.setTimeout(() => appendStaticBotForQuery(query), delay);
    } finally {
      setTyping(false);
    }
  };

  const handleSend = () => {
    const value = input.trim();
    if (!value) return;
    setInput("");
    void respondTo(value);
  };

  const handleSuggestion = async (id: string) => {
    const intent = intentById(id);
    if (!intent) return;
    setInput("");
    void respondTo(intent.label);
  };

  const handleReset = () => {
    setMessages([GREETING]);
  };

  const openWidget = () => {
    setOpen(true);
    setHasUnread(false);
    localStorage.setItem(SEEN_KEY, "1");
  };

  return (
    <>
      {!open ? (
        <button
          ref={launcherRef}
          onPointerDown={beginDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClick={() => {
            // A drag ends with a click event too — do not open the chat for it.
            if (dragged.current) {
              dragged.current = false;
              return;
            }
            openWidget();
          }}
          data-testid="amos-bot-launcher"
          style={{ right: pos.right, bottom: pos.bottom }}
          className="group fixed z-40 flex cursor-grab touch-none select-none items-center gap-2 rounded-full bg-[#002FA7] py-2.5 pl-3 pr-4 text-white shadow-xl shadow-[#002FA7]/30 transition-colors hover:bg-[#001B66] active:cursor-grabbing"
          title={`Open ${BOT_NAME} (drag to move)`}
        >
          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#002FA7]">
            <Bot className="h-4 w-4" strokeWidth={2.25} />
            {hasUnread ? (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#002FA7] bg-rose-500" />
            ) : null}
          </span>
          <span className="text-[13px] font-semibold tracking-tight">Ask {BOT_NAME}</span>
        </button>
      ) : null}

      {open ? (
        <div
          ref={panelRef}
          data-testid="amos-bot-panel"
          style={{ right: pos.right, bottom: pos.bottom }}
          className="animate-in fade-in slide-in-from-bottom-4 fixed z-40 flex h-[560px] max-h-[calc(100vh-3rem)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl duration-200"
        >
          <div
            onPointerDown={(e) => beginDrag(e, true)}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="flex cursor-grab touch-none select-none items-center gap-3 bg-zinc-950 px-4 py-3 text-white active:cursor-grabbing"
            title={`Drag to move ${BOT_NAME}`}
          >
            <div className="relative">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-950">
                <Bot className="h-5 w-5" strokeWidth={2.25} />
              </div>
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-zinc-950 bg-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[15px] font-bold">
                {BOT_NAME}
                <Sparkles className="h-3 w-3 text-amber-300" />
              </div>
              <div className="truncate text-[11px] text-zinc-400">{BOT_TAGLINE}</div>
            </div>
            <button
              onClick={handleReset}
              data-testid="amos-bot-reset"
              className="rounded-md p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white"
              title="Restart conversation"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="amos-bot-minimize"
              className="rounded-md p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white"
              title="Minimize"
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="amos-bot-close"
              className="rounded-md p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#FAFAFA] p-4" data-testid="amos-bot-messages">
            {messages.map((m, idx) => (
              <BotMessage key={idx} msg={m} onSuggestion={handleSuggestion} />
            ))}
            {typing ? (
              <div className="flex items-end gap-2" data-testid="amos-bot-typing">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#002FA7] text-white">
                  <Bot className="h-3.5 w-3.5" strokeWidth={2.5} />
                </div>
                <div className="flex items-center gap-1 rounded-2xl rounded-tl-sm border border-zinc-200 bg-white px-3 py-2">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-zinc-200 bg-white p-3">
            <div className="flex items-center gap-2 rounded-full border border-zinc-200 px-3 py-1.5 focus-within:border-[#002FA7] focus-within:ring-2 focus-within:ring-[#002FA7]/20">
              <MessageCircle className="h-4 w-4 flex-shrink-0 text-zinc-400" />
              <input
                data-testid="amos-bot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSend();
                }}
                placeholder={`Ask ${BOT_NAME} anything...`}
                className="flex-1 bg-transparent py-1.5 text-sm outline-none"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                data-testid="amos-bot-send"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-[#002FA7] text-white transition hover:bg-[#001B66] disabled:cursor-not-allowed disabled:opacity-30"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-2 text-center text-[10px] uppercase tracking-wider text-zinc-400">
              Powered by MarketIQ · AI when signed in (Gemini) · offline tips as fallback
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function BotMessage({
  msg,
  onSuggestion,
}: {
  msg: ChatMessage;
  onSuggestion: (id: string) => void;
}) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="amos-bot-msg-user">
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#002FA7] px-3 py-2 text-[13px] text-white shadow-sm">
          {msg.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2" data-testid="amos-bot-msg-bot">
      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#002FA7] text-white">
        <Bot className="h-3.5 w-3.5" strokeWidth={2.5} />
      </div>
      <div className="max-w-[85%]">
        <div className="whitespace-pre-line rounded-2xl rounded-tl-sm border border-zinc-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-zinc-800 shadow-sm">
          {(msg.text || "").split("\n").map((line, i) => (
            <Fragment key={i}>
              {i > 0 ? <br /> : null}
              {renderRich(line)}
            </Fragment>
          ))}
        </div>
        {msg.suggestedIds?.length ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {msg.suggestedIds.map((id) => {
              const intent = INTENTS.find((i) => i.id === id);
              if (!intent) return null;
              return (
                <button
                  key={id}
                  onClick={() => onSuggestion(id)}
                  data-testid={`amos-bot-suggest-${id}`}
                  className="rounded-full border border-[#002FA7]/40 bg-white px-2.5 py-1 text-[11px] font-medium text-[#002FA7] transition hover:bg-[#002FA7]/5"
                >
                  {intent.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
